import { defineStore } from 'pinia';
import { computed } from 'vue';

import { useEditingIngredientStore } from '@apps/main/modules/store/editingIngredientStore';
import { injectStrict } from '@apps/main/modules/helpers/injectStrict';
import {
  MapStateKey,
  ProductDetailStateKey,
  RecipeStateKey,
  TripStateKey,
} from '@apps/main/modules/state/state';
import { usePreferredTimeStore } from '@apps/main/modules/store/preferredTimeStore';

import type {
  DetailsStaticInfo,
  HeartedHotel,
  HotelBookableAndLockableOption,
  IngredientId,
} from '@libs/trip-planner-interfaces';
import { ProductSearchType } from '@libs/trip-planner-interfaces';

import type {
  HotelDetailsData,
  SelectStayEventPayload,
  ToggleHeartEventPayload,
} from '../../modules/hotelDetails';

import { useRouterStore } from './routerStore';

export const useHotelDetailsStore = defineStore('HotelDetailsStore', () => {
  const mapState = injectStrict(MapStateKey);
  const productDetailsState = injectStrict(ProductDetailStateKey);
  const tripState = injectStrict(TripStateKey);
  const recipeState = injectStrict(RecipeStateKey);
  const editingIngredientStore = useEditingIngredientStore();
  const routerStore = useRouterStore();
  const preferredTimeStore = usePreferredTimeStore();

  // Actions
  function openHotelDetails(
    hotelCode: string,
    ingredientId: IngredientId,
    shouldScrollToAvailability = false
  ): void {
    productDetailsState.shouldScrollToAvailability.value =
      shouldScrollToAvailability;
    productDetailsState.selectDestinationProduct(
      hotelCode,
      ingredientId,
      ProductSearchType.Accommodation
    );
  }

  // NOTE: Temporary method required until we will be able to
  // use hotel details panel on separated route on production.
  function openHotelDetailsForce(
    hotelCode: string,
    ingredientId: IngredientId,
    shouldScrollToAvailability = false
  ): void {
    productDetailsState.shouldScrollToAvailability.value =
      shouldScrollToAvailability;
    productDetailsState.selectDestinationProduct(
      hotelCode,
      ingredientId,
      ProductSearchType.Accommodation
    );
  }

  function selectStay(eventPayload: SelectStayEventPayload): void {
    const heartedHotel: HeartedHotel = {
      code: eventPayload.hotelCode,
      isRefundable: eventPayload.stayId.isRefundable,
      rooms: eventPayload.stayId.hotelRoomIds.map((room) => ({
        code: room.roomCode,
        boardBasis: room.boardBasis,
        roomTypeId: room.roomTypeId,
      })),
    };
    tripState.heartHotel(heartedHotel, eventPayload.ingredientId);
    mapState.mapShouldCenterOnMarkers.value = true;
    editingIngredientStore.doneEditingIngredient();
  }

  function toggleHeart(
    eventPayload: ToggleHeartEventPayload,
    finalizeEdit = true
  ): void {
    const heartedHotel: HeartedHotel = {
      code: eventPayload.hotelCode,
    };
    tripState.heartHotel(heartedHotel, eventPayload.ingredientId);

    if (finalizeEdit) {
      editingIngredientStore.doneEditingIngredient();
    }
  }

  function suggestAnotherDay(): void {
    routerStore.navigateToTripOverview();
    preferredTimeStore.clearPreferredDay();
  }

  // Getters
  const hotelDetailsData = computed<HotelDetailsData | undefined>(() => {
    const destinationProductDetail =
      productDetailsState.destinationProductDetail.value;

    if (!destinationProductDetail) {
      return;
    }

    const destination = productDetailsState.isDetailStaticInfo(
      destinationProductDetail.staticInfo
    )
      ? tripState.getMainPolygonFullName(
          destinationProductDetail.parentIngredientId,
          destinationProductDetail.staticInfo
        )
      : undefined;

    // TODO: Refactor code to avoid type assertions.
    // Intention is to have multiple types (one for each details),
    // to avoid union types, which make code hard to understand and confusing
    return {
      ingredientId: destinationProductDetail.parentIngredientId,
      hotelCode: destinationProductDetail.productCode,
      availability:
        destinationProductDetail.availability as HotelBookableAndLockableOption[],
      staticInfo: destinationProductDetail.staticInfo as DetailsStaticInfo,
      isHearted: destinationProductDetail.isHearted,
      isInItinerary: destinationProductDetail.isInItinerary,
      isMetaHotel: (
        destinationProductDetail.availability as HotelBookableAndLockableOption[]
      ).some((bookableOption) => bookableOption.isFromMeta),
      numberOfNights: destinationProductDetail.numberOfNights,
      destination,
      scrollToAvailability:
        productDetailsState.shouldScrollToAvailability.value,
      isSingleHotelRecipe:
        recipeState.recipe.value.length === 1 &&
        recipeState.hotelOnlyRecipe.value.length === 1,
    };
  });

  return {
    // Actions
    openHotelDetails,
    openHotelDetailsForce,
    selectStay,
    toggleHeart,
    suggestAnotherDay,

    // Getters
    hotelDetailsData,
  };
});
